import React, { useState } from 'react';
import LandingLayout from '../layouts/LandingLayout';
import { motion } from 'framer-motion';
import { Check, HelpCircle, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

interface PricingTier {
  name: string;
  price: string;
  description: string;
  features: string[];
  highlighted?: boolean;
  buttonText: string;
  priceId?: string;
}

const pricingTiers: PricingTier[] = [
  {
    name: 'Free',
    price: '$0',
    description: 'Perfect for individuals just getting started',
    features: [
      'Up to 10 meetings per month',
      'Basic calendar integration',
      'Email notifications',
      'Standard booking page',
      'Basic analytics'
    ],
    buttonText: 'Get Started',
  },
  {
    name: 'Pro',
    price: '$15',
    description: 'For professionals who need more power',
    features: [
      'Unlimited meetings',
      'Custom branding',
      'Multiple calendar integration',
      'Advanced analytics',
      'Group meetings',
      'Custom intake forms',
      'Priority support'
    ],
    highlighted: true,
    buttonText: 'Start Free Trial',
  },
  {
    name: 'Business',
    price: '$49',
    description: 'For teams and growing businesses',
    features: [
      'Everything in Pro',
      'Team management',
      'Admin dashboard',
      'API access',
      'Custom integrations',
      'Dedicated support',
      'SLA guarantee'
    ],
    buttonText: 'Contact Sales',
  }
];

const featureExplanations: { [key: string]: string } = {
  'Basic analytics': 'View basic meeting statistics and attendance rates',
  'Custom branding': 'Add your logo and customize colors to match your brand',
  'API access': 'Full access to our REST API for custom integrations',
  'Team management': 'Manage team members, permissions, and scheduling rules',
  'Custom integrations': 'Build and deploy custom integrations with our API',
  'SLA guarantee': '99.9% uptime guarantee with 24/7 monitoring'
};

const Pricing: React.FC = () => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');

  return (
    <LandingLayout>
      <div className="min-h-screen">
        {/* Hero Section */}
        <motion.section 
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="pt-32 pb-16 px-4 md:px-6 max-w-7xl mx-auto text-center"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/70">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Choose the perfect plan for your scheduling needs. All plans include a 14-day free trial.
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mb-12">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                billingPeriod === 'monthly' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                billingPeriod === 'yearly' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Yearly
              <span className="ml-2 text-xs bg-green-500/10 text-green-500 px-2 py-1 rounded-full">
                Save 20%
              </span>
            </button>
          </div>
        </motion.section>

        {/* Pricing Tiers */}
        <motion.section
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="py-16 px-4 md:px-6"
        >
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingTiers.map((tier, index) => (
              <motion.div
                key={tier.name}
                variants={fadeIn}
                className={`relative p-8 rounded-2xl border ${
                  tier.highlighted 
                    ? 'border-primary shadow-lg shadow-primary/10' 
                    : 'border-border'
                }`}
              >
                {tier.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-primary-foreground text-sm rounded-full">
                    Most Popular
                  </div>
                )}
                <div className="mb-6">
                  <h3 className="text-2xl font-bold mb-2">{tier.name}</h3>
                  <p className="text-muted-foreground">{tier.description}</p>
                </div>
                <div className="mb-6">
                  <span className="text-4xl font-bold">
                    {billingPeriod === 'yearly' && tier.price !== '$0'
                      ? `$${parseInt(tier.price.slice(1)) * 0.8}`
                      : tier.price}
                  </span>
                  <span className="text-muted-foreground">
                    {tier.price !== '$0' && `/${billingPeriod === 'monthly' ? 'mo' : 'yr'}`}
                  </span>
                </div>
                <ul className="space-y-4 mb-8">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">
                        {feature}
                        {featureExplanations[feature] && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <HelpCircle className="inline-block ml-1 h-4 w-4 text-muted-foreground/50" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="w-64">{featureExplanations[feature]}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={tier.name === 'Business' ? '/contact' : '/signup'}
                  className={`block w-full py-3 px-4 rounded-lg text-center transition-colors ${
                    tier.highlighted
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                      : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                  }`}
                >
                  {tier.buttonText}
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* FAQ Section */}
        <motion.section
          variants={fadeIn}
          initial="hidden"
          animate="visible" 
          className="py-16 px-4 md:px-6 bg-muted/30"
        >
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-12">Frequently Asked Questions</h2>
            <div className="space-y-8 text-left">
              <div>
                <h3 className="text-xl font-semibold mb-2">Can I change plans later?</h3>
                <p className="text-muted-foreground">
                  Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">What payment methods do you accept?</h3>
                <p className="text-muted-foreground">
                  We accept all major credit cards, PayPal, and bank transfers for annual plans.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2">Do you offer refunds?</h3>
                <p className="text-muted-foreground">
                  Yes, we offer a 30-day money-back guarantee for all paid plans.
                </p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section
          variants={fadeIn}
          initial="hidden"
          animate="visible"
          className="py-16 px-4 md:px-6 text-center"
        >
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold mb-4">Still have questions?</h2>
            <p className="text-muted-foreground mb-8">
              Our team is here to help you find the perfect plan for your needs.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center h-11 px-8 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Contact Sales
            </Link>
          </div>
        </motion.section>
      </div>
    </LandingLayout>
  );
};

export default Pricing; 